import React, { useState } from 'react';
import { FilterType, VariableType } from '../hooks/useDashboardState';
import { motion, AnimatePresence } from 'framer-motion';
import { Wind, Sun, Thermometer, Zap, ChevronDown, Check, Search } from 'lucide-react';
interface ZoneOption {
  name: string;
  code: string;
}
interface FilterBarProps {
  activeFilters: FilterType[];
  toggleFilter: (filter: FilterType) => void;
  activeZone: ZoneOption;
  setActiveZone: (zone: ZoneOption) => void;
  activeVariable: VariableType;
  setActiveVariable: (variable: VariableType) => void;
}
type WeightingType = 'Capacity (Wind)' | 'Capacity (Solar)' | 'None';
export function FilterBar({
  activeFilters,
  toggleFilter,
  activeZone,
  setActiveZone,
  activeVariable,
  setActiveVariable
}: FilterBarProps) {
  const [activeWeighting, setActiveWeighting] = useState<WeightingType>('Capacity (Wind)');
  const [isWeightingOpen, setIsWeightingOpen] = useState(false);
  const [isZoneOpen, setIsZoneOpen] = useState(false);
  const [zoneSearchQuery, setZoneSearchQuery] = useState('');
  const variables: {
    type: VariableType;
    icon: any;
  }[] = [{
    type: 'Wind',
    icon: Wind
  }, {
    type: 'Solar',
    icon: Sun
  }, {
    type: 'Temperature',
    icon: Thermometer
  }];
  const weightings: WeightingType[] = ['Capacity (Wind)', 'Capacity (Solar)', 'None'];
  // Comprehensive zone list based on the screenshot
  const zones: ZoneOption[] = [{
    name: 'Alaska (Southeast)',
    code: 'US-AK-SEAPA'
  }, {
    name: 'Arizona Public Service',
    code: 'US-SW-AZPS'
  }, {
    name: 'Associated Electric Cooperative',
    code: 'US-MIDW-AECI'
  }, {
    name: 'Austria',
    code: 'AT'
  }, {
    name: 'Avista Corporation',
    code: 'US-NW-AVA'
  }, {
    name: 'Balancing Authority of Northern California',
    code: 'US-CAL-BANC'
  }, {
    name: 'Bonneville Power Administration',
    code: 'US-NW-BPAT'
  }, {
    name: 'Belgium',
    code: 'BE'
  }, {
    name: 'Bulgaria',
    code: 'BG'
  }, {
    name: 'California ISO',
    code: 'US-CAL-CISO'
  }, {
    name: 'Chelan County PUD',
    code: 'US-NW-CHPD'
  }, {
    name: 'City of Tallahassee',
    code: 'US-FLA-TAL'
  }, {
    name: 'Croatia',
    code: 'HR'
  }, {
    name: 'Cyprus',
    code: 'CY'
  }, {
    name: 'Czech Republic',
    code: 'CZ'
  }, {
    name: 'Denmark (DK1)',
    code: 'DK-DK1'
  }, {
    name: 'Denmark (DK2)',
    code: 'DK-DK2'
  }, {
    name: 'Duke Energy Carolinas',
    code: 'US-CAR-DUK'
  }, {
    name: 'Duke Energy Progress East',
    code: 'US-CAR-DUKPROGEAST'
  }, {
    name: 'Duke Energy Progress West',
    code: 'US-CAR-DUKPROGWEST'
  }, {
    name: 'El Paso Electric',
    code: 'US-TEX-EPE'
  }, {
    name: 'Electric Reliability Council of Texas',
    code: 'US-TEX-ERCO'
  }, {
    name: 'Estonia',
    code: 'EE'
  }, {
    name: 'Finland',
    code: 'FI'
  }, {
    name: 'France',
    code: 'FR'
  }, {
    name: 'Germany',
    code: 'DE'
  }, {
    name: 'Germany (50Hertz)',
    code: 'DE-50HZ'
  }, {
    name: 'Germany (Amprion)',
    code: 'DE-AMP'
  }, {
    name: 'Germany (TenneT)',
    code: 'DE-TEN'
  }, {
    name: 'Germany (TransnetBW)',
    code: 'DE-TBW'
  }, {
    name: 'Great Britain',
    code: 'GB'
  }, {
    name: 'Greece',
    code: 'GR'
  }, {
    name: 'Hawaii',
    code: 'US-HI'
  }, {
    name: 'Hungary',
    code: 'HU'
  }, {
    name: 'Idaho Power Company',
    code: 'US-NW-IPCO'
  }, {
    name: 'Ireland',
    code: 'IE'
  }, {
    name: 'Italy',
    code: 'IT'
  }, {
    name: 'Italy (Calabria)',
    code: 'IT-CAL'
  }, {
    name: 'Italy (Central North)',
    code: 'IT-CNO'
  }, {
    name: 'Italy (Central South)',
    code: 'IT-CSO'
  }, {
    name: 'Italy (North)',
    code: 'IT-NO'
  }, {
    name: 'Italy (Sardinia)',
    code: 'IT-SAR'
  }, {
    name: 'Italy (Sicily)',
    code: 'IT-SIC'
  }, {
    name: 'Italy (South)',
    code: 'IT-SO'
  }, {
    name: 'Latvia',
    code: 'LV'
  }, {
    name: 'Lithuania',
    code: 'LT'
  }, {
    name: 'Luxembourg',
    code: 'LU'
  }, {
    name: 'Malta',
    code: 'MT'
  }, {
    name: 'Midcontinent ISO',
    code: 'US-MIDW-MISO'
  }, {
    name: 'Netherlands',
    code: 'NL'
  }, {
    name: 'New York ISO',
    code: 'US-NY-NYIS'
  }, {
    name: 'Northern Ireland',
    code: 'GB-NIR'
  }, {
    name: 'NorthWestern Energy',
    code: 'US-NW-NWMT'
  }, {
    name: 'Norway (NO1)',
    code: 'NO-NO1'
  }, {
    name: 'Norway (NO2)',
    code: 'NO-NO2'
  }, {
    name: 'Norway (NO3)',
    code: 'NO-NO3'
  }, {
    name: 'Norway (NO4)',
    code: 'NO-NO4'
  }, {
    name: 'Norway (NO5)',
    code: 'NO-NO5'
  }, {
    name: 'PacifiCorp East',
    code: 'US-NW-PACE'
  }, {
    name: 'PacifiCorp West',
    code: 'US-NW-PACW'
  }, {
    name: 'PJM Interconnection',
    code: 'US-MIDA-PJM'
  }, {
    name: 'Poland',
    code: 'PL'
  }, {
    name: 'Portugal',
    code: 'PT'
  }, {
    name: 'Public Service Company of Colorado',
    code: 'US-NW-PSCO'
  }, {
    name: 'Romania',
    code: 'RO'
  }, {
    name: 'Salt River Project',
    code: 'US-SW-SRP'
  }, {
    name: 'Slovakia',
    code: 'SK'
  }, {
    name: 'Slovenia',
    code: 'SI'
  }, {
    name: 'Southeast',
    code: 'US-SE'
  }, {
    name: 'Southern Company',
    code: 'US-SE-SOCO'
  }, {
    name: 'Southwest Power Pool',
    code: 'US-CENT-SPP'
  }, {
    name: 'Spain',
    code: 'ES'
  }, {
    name: 'Sweden (SE1)',
    code: 'SE-SE1'
  }, {
    name: 'Sweden (SE2)',
    code: 'SE-SE2'
  }, {
    name: 'Sweden (SE3)',
    code: 'SE-SE3'
  }, {
    name: 'Sweden (SE4)',
    code: 'SE-SE4'
  }, {
    name: 'Switzerland',
    code: 'CH'
  }, {
    name: 'Tennessee Valley Authority',
    code: 'US-TEN-TVA'
  }, {
    name: 'Turkey',
    code: 'TR'
  }];
  // Filter zones based on search query
  const filteredZones = zones.filter(zone => zone.name.toLowerCase().includes(zoneSearchQuery.toLowerCase()) || zone.code.toLowerCase().includes(zoneSearchQuery.toLowerCase()));
  const handleVariableChange = (type: VariableType) => {
    setActiveVariable(type);
    // Auto-update weighting based on variable
    if (type === 'Wind') setActiveWeighting('Capacity (Wind)');else if (type === 'Solar') setActiveWeighting('Capacity (Solar)');else setActiveWeighting('None');
  };
  return <div className="flex flex-col lg:flex-row lg:items-center gap-4 lg:gap-3 py-4 overflow-visible custom-scrollbar relative z-30" data-id="element-278">
      {/* Variable Selector */}
      <div className="flex items-center gap-2" data-id="element-279">
        <span className="text-xs font-semibold text-slate-500 uppercase tracking-wider whitespace-nowrap" data-id="element-280">
          Variable
        </span>
        <div className="flex bg-slate-100/80 p-1 rounded-lg border border-slate-200/50 relative overflow-x-auto" data-id="element-281">
          {variables.map(({
          type,
          icon: Icon
        }) => {
          const isActive = activeVariable === type;
          return <motion.button key={type} whileTap={{
            scale: 0.95
          }} onClick={() => handleVariableChange(type)} className={`relative flex items-center gap-2 px-3 py-1.5 rounded-md text-xs font-medium transition-colors whitespace-nowrap ${isActive ? 'text-slate-900' : 'text-slate-500 hover:text-slate-900'}`} data-id="element-282">
                {isActive && <motion.div layoutId="variableBackground" className="absolute inset-0 bg-white rounded-md shadow-sm ring-1 ring-black/5" transition={{
              type: 'spring',
              bounce: 0.2,
              duration: 0.6
            }} data-id="element-283" />}
                <Icon size={12} className={`relative z-10 ${isActive ? 'text-[#ff006b]' : ''}`} data-id="element-284" />
                <span className="relative z-10" data-id="element-285">{type}</span>
              </motion.button>;
        })}
        </div>
      </div>

      <div className="hidden lg:block w-px h-6 bg-slate-200 mx-1" data-id="element-286" />

      <div className="flex flex-wrap items-center gap-x-3 gap-y-2" data-id="element-287">
        {/* Weighting Dropdown */}
        <div className="relative flex items-center" data-id="element-288">
          <span className="text-xs font-semibold text-slate-500 uppercase tracking-wider mr-2 whitespace-nowrap" data-id="element-289">
            Weighting
          </span>
          <button onClick={() => {
          setIsWeightingOpen(!isWeightingOpen);
          setIsZoneOpen(false);
        }} className="inline-flex items-center gap-2 px-3 py-1.5 rounded-lg text-xs font-medium bg-white/80 border border-slate-200 text-slate-700 hover:border-slate-300 hover:shadow-sm transition-all min-w-[140px] justify-between" data-id="element-290">
            <div className="flex items-center gap-2" data-id="element-291">
              <Zap size={12} className="text-slate-400" data-id="element-292" />
              <span className="truncate" data-id="element-293">{activeWeighting}</span>
            </div>
            <ChevronDown size={12} className={`transition-transform flex-shrink-0 ${isWeightingOpen ? 'rotate-180' : ''}`} data-id="element-294" />
          </button>

          <AnimatePresence data-id="element-295">
            {isWeightingOpen && <motion.div initial={{
            opacity: 0,
            y: 5
          }} animate={{
            opacity: 1,
            y: 0
          }} exit={{
            opacity: 0,
            y: 5
          }} className="absolute top-full left-0 lg:left-16 mt-1 w-48 bg-white rounded-lg shadow-xl border border-slate-100 py-1 z-50" data-id="element-296">
                {weightings.map(w => <button key={w} onClick={() => {
              setActiveWeighting(w);
              setIsWeightingOpen(false);
            }} className="w-full flex items-center justify-between px-3 py-2 text-xs text-left hover:bg-slate-50 text-slate-700" data-id="element-297">
                    {w}
                    {activeWeighting === w && <Check size={12} className="text-[#ff006b]" data-id="element-298" />}
                  </button>)}
              </motion.div>}
          </AnimatePresence>
        </div>

        <div className="hidden lg:block w-px h-6 bg-slate-200" data-id="element-299" />

        {/* Zone Dropdown with Search */}
        <div className="relative overflow-visible flex items-center" data-id="element-300">
          <span className="text-xs font-semibold text-slate-500 uppercase tracking-wider mr-2 whitespace-nowrap" data-id="element-301">
            Zone
          </span>
          <button onClick={() => {
          setIsZoneOpen(!isZoneOpen);
          setIsWeightingOpen(false);
          setZoneSearchQuery('');
        }} className="inline-flex items-center gap-2 px-3 py-1.5 rounded-lg text-xs font-medium bg-white/80 border border-slate-200 text-slate-700 hover:border-slate-300 hover:shadow-sm transition-all min-w-[120px] justify-between" data-id="element-302">
            <span className="truncate" data-id="element-303">{activeZone.name}</span>
            <ChevronDown size={12} className={`transition-transform flex-shrink-0 ${isZoneOpen ? 'rotate-180' : ''}`} data-id="element-304" />
          </button>

          <AnimatePresence data-id="element-305">
            {isZoneOpen && <motion.div initial={{
            opacity: 0,
            y: 5
          }} animate={{
            opacity: 1,
            y: 0
          }} exit={{
            opacity: 0,
            y: 5
          }} className="absolute top-full left-0 lg:left-12 mt-1 w-80 max-w-[calc(100vw-2rem)] bg-white rounded-lg shadow-xl border border-slate-100 z-50 overflow-hidden" data-id="element-306">
                {/* Search Input */}
                <div className="p-3 border-b border-slate-100" data-id="element-307">
                  <div className="relative" data-id="element-308">
                    <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" data-id="element-309" />
                    <input type="text" placeholder="Search zones..." value={zoneSearchQuery} onChange={e => setZoneSearchQuery(e.target.value)} className="w-full pl-9 pr-3 py-2 text-xs border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-pink-500/20 focus:border-pink-500/50" data-id="element-310" />
                  </div>
                </div>

                {/* Clear Selection Option */}
                <button onClick={() => {
              setActiveZone({
                name: 'Germany',
                code: 'DE'
              });
              setIsZoneOpen(false);
              setZoneSearchQuery('');
            }} className="w-full px-4 py-2.5 text-xs text-left hover:bg-slate-50 text-slate-700 font-medium border-b border-slate-100" data-id="element-311">
                  Reset to Germany
                </button>

                {/* Zone List */}
                <div className="max-h-80 overflow-y-auto custom-scrollbar" data-id="element-312">
                  {filteredZones.length > 0 ? filteredZones.map(zone => <button key={zone.code} onClick={() => {
                setActiveZone(zone);
                setIsZoneOpen(false);
                setZoneSearchQuery('');
              }} className="w-full px-4 py-2.5 text-left hover:bg-slate-50 transition-colors group" data-id="element-313">
                        <div className="flex items-start justify-between gap-2" data-id="element-314">
                          <div className="flex-1 min-w-0" data-id="element-315">
                            <p className="text-sm font-medium text-slate-900 group-hover:text-pink-700 transition-colors truncate" data-id="element-316">
                              {zone.name}
                            </p>
                            <p className="text-xs text-slate-500 mt-0.5 font-mono" data-id="element-317">
                              {zone.code}
                            </p>
                          </div>
                          {activeZone.code === zone.code && <Check size={14} className="text-[#ff006b] mt-0.5 flex-shrink-0" data-id="element-318" />}
                        </div>
                      </button>) : <div className="px-4 py-8 text-center" data-id="element-319">
                      <p className="text-sm text-slate-500" data-id="element-320">No zones found</p>
                      <p className="text-xs text-slate-400 mt-1" data-id="element-321">
                        Try a different search term
                      </p>
                    </div>}
                </div>
              </motion.div>}
          </AnimatePresence>
        </div>
      </div>
    </div>;
}